/**
 * Frontend plain-English mapper for preflight/render events.
 *
 * The backend already emits friendly messages on every issue / warning
 * (see `services/render/final_render_pipeline.py`). This module is a
 * defensive last mile: if any component ever bubbles up a raw
 * SCREAMING_SNAKE code (regression, third-party service, etc.), we
 * render a jargon-free string here rather than showing the code to a
 * layman.
 *
 * Rule of thumb: NEVER render `entry.kind`, `entry.code`, or any
 * ALL_CAPS token to end users. Always route through this helper.
 */

const PLAIN_ENGLISH_FALLBACKS = {
    MISSING_VOICE:            "We're still preparing the voiceover for this scene.",
    MISSING_VISUAL:           "We're still preparing the visual for this scene.",
    MISSING_TRAINER_REFERENCE:"We're still preparing the trainer for this scene.",
    TRAINER_PREPARING:        "We're still preparing the trainer for this scene.",
    USING_TRAINER_FALLBACK:   "We're finishing this scene — just a moment.",
    HEYGEN_KEY_MISSING:       "We're still preparing the trainer for this scene.",
    MODULE_STATE:             "This module isn't ready to render yet.",
    MODULE_MISSING:           "This module can no longer be found.",
    STALE_RUNNER:             "The previous build stopped. We'll pick it back up shortly.",
    RESTART_ORPHAN:           "The previous build was interrupted. Please try again.",
};

const CODE_LIKE = /^[A-Z][A-Z0-9_]{3,}$/;

/**
 * Return a user-safe string for a preflight/render entry.
 *
 * Priority:
 *   1. `entry.message` if it exists AND is not a raw code (does NOT
 *      look like SCREAMING_SNAKE_CASE).
 *   2. Fallback map keyed on `entry.kind` / `entry.code`.
 *   3. Generic "Preparing scene X — one moment." (uses
 *      `entry.scene_number` when available).
 */
export function plainEnglishMessage(entry) {
    if (!entry) return "One moment while we get things ready.";
    const msg = (entry.message || "").trim();
    if (msg && !CODE_LIKE.test(msg)) return msg;
    const key = entry.kind || entry.code;
    if (key && PLAIN_ENGLISH_FALLBACKS[key]) return PLAIN_ENGLISH_FALLBACKS[key];
    if (entry.scene_number) return `Preparing scene ${entry.scene_number} — one moment.`;
    return "One moment while we get things ready.";
}

// Scenes that will use an animated motion graphic are expected, not problems.
export const isMotionNote = (entry) => (entry?.kind || entry?.code) === "MOTION_GRAPHIC";

// Auto-fix-resolvable issue kinds. When ANY of these appear in
// preflight.issues, the frontend fires a single silent /auto-fix call.
export const AUTO_FIX_RESOLVABLE_ISSUES = new Set([
    "MISSING_VOICE",
    "MISSING_VISUAL",
    "MISSING_TRAINER_REFERENCE",   // legacy, kept for prod boot compat
    "TRAINER_PREPARING",
]);

export const AUTO_FIX_RESOLVABLE_WARNINGS = new Set([
    "USING_TRAINER_FALLBACK",
    "TRAINER_PREPARING",
]);
